import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { supabase } from '../../lib/supabase';

// ── Date / format helpers ─────────────────────────────────────────────────────
const MONTHS_MAP = {
  Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',
  Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12',
};

const parseUlipDate = (s) => {
  if (!s) return null;
  const p = s.split('-');
  if (p.length === 3 && MONTHS_MAP[p[1]])
    return `${p[2]}-${MONTHS_MAP[p[1]]}-${p[0].padStart(2, '0')}`;
  return s;
};

const todayStr  = () => new Date().toISOString().split('T')[0];
const in30Days  = () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const complianceStatus = (dateStr) => {
  const d = parseUlipDate(dateStr);
  if (!d) return 'unknown';
  if (d < todayStr())  return 'expired';
  if (d < in30Days())  return 'expiring';
  return 'ok';
};

const fmtUlipDate = (dateStr) => {
  const iso = parseUlipDate(dateStr);
  if (!iso) return '—';
  const [y, m, day] = iso.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${day} ${names[parseInt(m, 10) - 1]} ${y}`;
};

const fmtDate = (val) => {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return val;
  }
};

const fmtCurrency = (n) =>
  n != null && n !== '' ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

// ── Small components ──────────────────────────────────────────────────────────
const Badge = ({ text, color, bg }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
    style={{ color, background: bg }}
  >
    {text}
  </span>
);

const listingBadge = (status) => {
  const map = {
    Available: { text: 'Available', color: '#166534', bg: '#dcfce7' },
    Assigned:  { text: 'Assigned',  color: '#1e40af', bg: '#dbeafe' },
    Completed: { text: 'Completed', color: '#374151', bg: '#f3f4f6' },
  };
  const s = map[status] || { text: status || '—', color: '#374151', bg: '#f3f4f6' };
  return <Badge {...s} />;
};

const complianceBadge = (worst) => {
  if (worst === 'ok')       return <Badge text="✓ Compliant"     color="#166534" bg="#dcfce7" />;
  if (worst === 'expiring') return <Badge text="⚠ Expiring Soon" color="#92400e" bg="#fef3c7" />;
  if (worst === 'expired')  return <Badge text="✗ Expired"       color="#991b1b" bg="#fee2e2" />;
  return <Badge text="Verifying" color="#374151" bg="#f3f4f6" />;
};

const paymentBadge = (status) =>
  status === 'paid'
    ? <Badge text="Paid"    color="#166534" bg="#dcfce7" />
    : <Badge text="Pending" color="#92400e" bg="#fef3c7" />;

// ── Add Truck Form ────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  vehicle_number: '', capacity_mt: '', driver_name: '', driver_mobile: '',
  rate_per_ton: '', loading_location: '', delivery_destination: '', arrival_date: '',
};

const AddTruckForm = ({ profile, onSuccess }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successId, setSuccessId] = useState('');

  const set = (field, val) => { setForm(f => ({ ...f, [field]: val })); setFormError(''); };

  const handleSubmit = async () => {
    const { vehicle_number, capacity_mt, driver_name, driver_mobile, rate_per_ton, loading_location, delivery_destination, arrival_date } = form;
    if (!vehicle_number.trim())                          { setFormError('Vehicle number is required.'); return; }
    if (!capacity_mt)                                    { setFormError('Capacity is required.'); return; }
    if (!driver_name.trim())                             { setFormError('Driver name is required.'); return; }
    if (driver_mobile.replace(/\D/g,'').length < 10)    { setFormError('Enter a valid 10-digit driver mobile.'); return; }
    if (!rate_per_ton)                                   { setFormError('Freight rate is required.'); return; }
    if (!loading_location.trim())                        { setFormError('Loading location is required.'); return; }
    if (!delivery_destination.trim())                    { setFormError('Delivery destination is required.'); return; }
    if (!arrival_date)                                   { setFormError('Available from date is required.'); return; }

    setSubmitting(true);
    setFormError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token;
      if (!jwt) { setFormError('Session expired. Please log in again.'); setSubmitting(false); return; }

      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const res = await fetch(`${SUPABASE_URL}/functions/v1/add-truck-pwa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          vehicle_number: vehicle_number.toUpperCase().trim(),
          capacity_mt: Number(capacity_mt),
          driver_name: driver_name.trim(),
          driver_mobile: driver_mobile.replace(/\D/g, ''),
          rate_per_ton: Number(rate_per_ton),
          loading_location: loading_location.trim(),
          delivery_destination: delivery_destination.trim(),
          arrival_date,
        }),
      });

      const data = await res.json();
      if (!data.success) { setFormError(data.error || 'Submission failed. Please try again.'); return; }

      setSuccessId(data.listing_id);
      setForm(EMPTY_FORM);
      if (onSuccess) setTimeout(onSuccess, 2200);
    } catch {
      setFormError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (successId) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: '#dcfce7' }}>
          <Icon name="CheckCircle" size={36} color="#16a34a" />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-1">Truck Listed!</h3>
        <p className="text-sm text-muted-foreground mb-1">
          ID: <span className="font-mono font-semibold">{successId}</span>
        </p>
        <p className="text-xs text-muted-foreground">We'll contact you on WhatsApp when a load is assigned. Taking you to your listings…</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <p className="text-sm text-muted-foreground mb-5">
        Listing as: <span className="font-semibold text-foreground">{profile?.company_name || profile?.full_name || '—'}</span>
      </p>

      {formError && (
        <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: '#fee2e2', color: '#991b1b' }}>
          {formError}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Vehicle Number *</label>
          <input
            value={form.vehicle_number}
            onChange={e => set('vehicle_number', e.target.value.toUpperCase())}
            placeholder="e.g. MH12AB1234"
            className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-mono uppercase"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Capacity (MT) *</label>
            <input
              type="number" value={form.capacity_mt}
              onChange={e => set('capacity_mt', e.target.value)}
              placeholder="e.g. 35"
              className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Rate ₹/ton *</label>
            <input
              type="number" value={form.rate_per_ton}
              onChange={e => set('rate_per_ton', e.target.value)}
              placeholder="e.g. 1000"
              className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Driver Name *</label>
            <input
              value={form.driver_name}
              onChange={e => set('driver_name', e.target.value)}
              placeholder="As on licence"
              className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Driver Mobile *</label>
            <input
              type="tel" value={form.driver_mobile}
              onChange={e => set('driver_mobile', e.target.value)}
              placeholder="10-digit number" maxLength={10}
              className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Loading Location *</label>
          <input
            value={form.loading_location}
            onChange={e => set('loading_location', e.target.value)}
            placeholder="e.g. Kolhapur, Maharashtra"
            className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Delivery Destination *</label>
          <input
            value={form.delivery_destination}
            onChange={e => set('delivery_destination', e.target.value)}
            placeholder="e.g. Pune, Maharashtra"
            className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Available From *</label>
          <input
            type="date" value={form.arrival_date}
            onChange={e => set('arrival_date', e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: '#1e4a72' }}
        >
          {submitting ? 'Submitting…' : 'List My Truck'}
        </button>
      </div>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <a
        href="https://wa.me/917989953468?text=Add+Truck"
        target="_blank"
        rel="noreferrer"
        className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
        style={{ background: '#25D366' }}
      >
        <Icon name="MessageCircle" size={18} color="#fff" />
        Register via WhatsApp instead
      </a>
      <p className="text-xs text-center text-muted-foreground mt-3">
        Say <span className="font-mono font-semibold">Hi</span> on WhatsApp: +91 79899 53468
      </p>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const TransporterDashboard = () => {
  const [activeTab,    setActiveTab]    = useState('add');
  const [profile,      setProfile]      = useState(null);
  const [listings,     setListings]     = useState([]);
  const [vehicles,     setVehicles]     = useState({});   // keyed by vehicle_number
  const [assignments,  setAssignments]  = useState([]);
  const [bookings,     setBookings]     = useState({});   // keyed by booking id
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Auth
      const { data: { user }, error: authErr } = await supabase.auth.getUser();
      if (authErr || !user) throw new Error('Not authenticated');

      // 2. Profile
      const { data: prof } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, company_name, mobile_phone, role')
        .eq('user_id', user.id)
        .maybeSingle();
      setProfile(prof);

      // 3. My listings (all-time, newest first)
      const { data: listData } = await supabase
        .from('truck_listings')
        .select('*')
        .eq('transporter_user_id', user.id)
        .order('created_at', { ascending: false });
      const myListings = listData || [];
      setListings(myListings);

      // 4. Vehicle compliance — only 3 dates shown to transporter
      const vNums = [...new Set(myListings.map(l => l.vehicle_number).filter(Boolean))];
      if (vNums.length > 0) {
        const { data: vData } = await supabase
          .from('vehicles')
          .select('vehicle_number, rc_regn_upto, rc_insurance_upto, rc_fit_upto, verification_pass')
          .in('vehicle_number', vNums);
        const vMap = {};
        (vData || []).forEach(v => { vMap[v.vehicle_number] = v; });
        setVehicles(vMap);
      }

      // 5. Assignments via my listing IDs
      const lIds = myListings.map(l => l.id).filter(Boolean);
      if (lIds.length > 0) {
        const { data: aData } = await supabase
          .from('order_truck_assignments')
          .select('*')
          .in('truck_listing_id', lIds)
          .order('assigned_at', { ascending: false });
        const myAssignments = aData || [];
        setAssignments(myAssignments);

        // 6. Booking details for those assignments
        const bIds = [...new Set(myAssignments.map(a => a.booking_id).filter(Boolean))];
        if (bIds.length > 0) {
          const { data: bData } = await supabase
            .from('buyer_bookings')
            .select('id, readable_booking_id, factory_name, loading_from, destination')
            .in('id', bIds);
          const bMap = {};
          (bData || []).forEach(b => { bMap[b.id] = b; });
          setBookings(bMap);
        }
      }
    } catch (err) {
      console.error('TransporterDashboard fetchAll error:', err);
      setError(err.message || 'Failed to load dashboard');
    }
    setLoading(false);
  };

  // ── Derived stats ─────────────────────────────────────────────────────────
  const totalListings  = listings.length;
  const activeTrips    = assignments.filter(a => a.status === 'assigned').length;
  const totalEarnings  = assignments.reduce((s, a) => s + Number(a.freight_amount || 0), 0);
  const pendingPayment = assignments
    .filter(a => a.transporter_payment_status !== 'paid')
    .reduce((s, a) => s + Number(a.freight_amount || 0), 0);

  // ── Compliance helper ─────────────────────────────────────────────────────
  const getCompliance = (vehicleNumber) => {
    const v = vehicles[vehicleNumber];
    if (!v) return null;
    const ins  = complianceStatus(v.rc_insurance_upto);
    const fit  = complianceStatus(v.rc_fit_upto);
    const rc   = complianceStatus(v.rc_regn_upto);
    const all  = [ins, fit, rc];
    const worst = all.includes('expired')  ? 'expired'
                : all.includes('expiring') ? 'expiring'
                : all.every(x => x === 'ok') ? 'ok'
                : 'unknown';
    return { ins, fit, rc, worst, v };
  };

  // ── Tab: Add a Truck ──────────────────────────────────────────────────────
  const renderAddTruck = () => (
    <AddTruckForm
      profile={profile}
      onSuccess={() => {
        fetchAll();
        setActiveTab('listings');
      }}
    />
  );

  // ── Tab: My Listed Trucks ────────────────────────────────────────────────
  const renderListings = () => {
    if (listings.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="Truck" size={48} color="#9ca3af" />
          <p className="text-muted-foreground text-sm mt-4">No trucks listed yet.</p>
          <button
            onClick={() => setActiveTab('add')}
            className="mt-3 text-sm text-primary underline underline-offset-2"
          >
            List your first truck →
          </button>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {listings.map(l => {
          const comp = getCompliance(l.vehicle_number);
          return (
            <div key={l.id} className="bg-card border border-border rounded-xl p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-base font-bold text-foreground">{l.vehicle_number}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {l.loading_location}
                    {l.delivery_destination ? ` → ${l.delivery_destination}` : ''}
                  </p>
                </div>
                {listingBadge(l.status)}
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs mb-3">
                <div>
                  <p className="text-muted-foreground">Capacity</p>
                  <p className="font-semibold text-foreground">{l.capacity_mt} MT</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Rate</p>
                  <p className="font-semibold text-foreground">₹{l.rate_per_ton}/ton</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Available From</p>
                  <p className="font-semibold text-foreground">{fmtDate(l.arrival_date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Listed On</p>
                  <p className="font-semibold text-foreground">{fmtDate(l.created_at)}</p>
                </div>
              </div>

              {/* Compliance strip — 3 dates only */}
              <div className="border-t border-border pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground">Vehicle Compliance</span>
                  {comp ? complianceBadge(comp.worst) : <Badge text="Pending" color="#374151" bg="#f3f4f6" />}
                </div>
                {comp ? (
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {[
                      { label: 'Insurance', val: fmtUlipDate(comp.v.rc_insurance_upto), st: comp.ins },
                      { label: 'Fitness',   val: fmtUlipDate(comp.v.rc_fit_upto),       st: comp.fit },
                      { label: 'RC Valid',  val: fmtUlipDate(comp.v.rc_regn_upto),      st: comp.rc  },
                    ].map(({ label, val, st }) => (
                      <div key={label}>
                        <p className="text-muted-foreground">{label}</p>
                        <p
                          className="font-medium"
                          style={{
                            color: st === 'expired'  ? '#dc2626'
                                 : st === 'expiring' ? '#d97706'
                                 : '#111827',
                          }}
                        >
                          {val}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">VAHAN verification pending</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Tab: Ongoing Trips ───────────────────────────────────────────────────
  const renderTrips = () => {
    const active = assignments.filter(a => a.status === 'assigned');
    if (active.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="Navigation" size={48} color="#9ca3af" />
          <p className="text-muted-foreground text-sm mt-4">No active assignments at the moment.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Loads assigned to your trucks will appear here.
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {active.map(a => {
          const bk = bookings[a.booking_id] || {};
          const freight = Number(a.freight_amount) || (Number(a.rate_per_ton) * Number(a.assigned_quantity));
          return (
            <div key={a.id} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-base font-bold text-foreground">{a.vehicle_number}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {bk.factory_name || a.loading_location || '—'} → {a.destination || bk.destination || '—'}
                  </p>
                </div>
                <Badge text="Assigned" color="#1e40af" bg="#dbeafe" />
              </div>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
                <div>
                  <p className="text-muted-foreground">Booking ID</p>
                  <p className="font-semibold text-foreground font-mono text-xs">
                    {bk.readable_booking_id || a.readable_booking_id || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Quantity</p>
                  <p className="font-semibold text-foreground">{a.assigned_quantity} MT</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Driver</p>
                  <p className="font-semibold text-foreground">{a.driver_name || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Driver Mobile</p>
                  <p className="font-semibold text-foreground">{a.driver_phone || '—'}</p>
                </div>
                {a.do_number ? (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">DO Number</p>
                    <p className="font-semibold text-foreground font-mono">{a.do_number}</p>
                  </div>
                ) : null}
                <div>
                  <p className="text-muted-foreground">Assigned On</p>
                  <p className="font-semibold text-foreground">{fmtDate(a.assigned_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Freight</p>
                  <p className="font-semibold text-foreground">{fmtCurrency(freight)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Tab: My Earnings ─────────────────────────────────────────────────────
  const renderEarnings = () => {
    const paid    = assignments
      .filter(a => a.transporter_payment_status === 'paid')
      .reduce((s, a) => s + Number(a.freight_amount || 0), 0);
    const pending = assignments
      .filter(a => a.transporter_payment_status !== 'paid')
      .reduce((s, a) => s + Number(a.freight_amount || 0), 0);

    return (
      <div>
        {/* Summary strip */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            { label: 'Total Earned',    value: fmtCurrency(totalEarnings), color: '#166534', bg: '#dcfce7' },
            { label: 'Pending Payment', value: fmtCurrency(pending),       color: '#92400e', bg: '#fef3c7' },
            { label: 'Paid Out',        value: fmtCurrency(paid),          color: '#1e40af', bg: '#dbeafe' },
            { label: 'Total Trips',     value: String(assignments.length), color: '#374151', bg: '#f3f4f6' },
          ].map(s => (
            <div key={s.label} className="rounded-xl p-4" style={{ background: s.bg }}>
              <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
              <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {assignments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Icon name="IndianRupee" size={48} color="#9ca3af" />
            <p className="text-muted-foreground text-sm mt-4">No earnings recorded yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Earnings appear once your truck is assigned to a load.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {assignments.map(a => {
              const bk = bookings[a.booking_id] || {};
              const freight = Number(a.freight_amount) || (Number(a.rate_per_ton) * Number(a.assigned_quantity));
              return (
                <div key={a.id} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-bold text-foreground">{a.vehicle_number}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {bk.readable_booking_id || a.readable_booking_id || '—'}
                      </p>
                    </div>
                    {paymentBadge(a.transporter_payment_status)}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {a.assigned_quantity} MT × ₹{a.rate_per_ton}/ton
                    </span>
                    <p className="font-bold text-foreground">{fmtCurrency(freight)}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDate(a.assigned_at)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'add',      label: 'Add a Truck',     icon: 'Plus'        },
    { id: 'listings', label: 'My Listed Trucks', icon: 'Truck'       },
    { id: 'trips',    label: 'Ongoing Trips',    icon: 'Navigation'  },
    { id: 'earnings', label: 'My Earnings',      icon: 'IndianRupee' },
  ];

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm text-muted-foreground">Loading your dashboard…</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="AlertCircle" size={40} color="#dc2626" />
          <p className="text-sm text-red-600 font-medium mt-4">{error}</p>
          <button onClick={fetchAll} className="mt-4 text-sm text-primary underline">
            Retry
          </button>
        </div>
      );
    }
    switch (activeTab) {
      case 'add':      return renderAddTruck();
      case 'listings': return renderListings();
      case 'trips':    return renderTrips();
      case 'earnings': return renderEarnings();
      default:         return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Transporter Dashboard - SugarHathi</title>
        <meta
          name="description"
          content="Manage your truck listings, assignments and earnings on SugarHathi"
        />
      </Helmet>
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      <div className="main-content bg-background">
        <div className="max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">

          {/* Page header */}
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">
              Transporter Dashboard
            </h1>
            <p className="text-sm text-muted-foreground">
              {profile?.company_name || profile?.full_name || 'Welcome'} — Manage your trucks and earnings
            </p>
          </div>

          {/* Stats strip */}
          {!loading && !error && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Trucks Listed',   value: totalListings,           icon: 'Truck',        color: '#1e40af', bg: '#dbeafe' },
                { label: 'Active Trips',    value: activeTrips,             icon: 'Navigation',   color: '#166534', bg: '#dcfce7' },
                { label: 'Total Earnings',  value: fmtCurrency(totalEarnings),  icon: 'IndianRupee',  color: '#166534', bg: '#dcfce7' },
                { label: 'Pending Payment', value: fmtCurrency(pendingPayment), icon: 'Clock',        color: '#92400e', bg: '#fef3c7' },
              ].map(s => (
                <div key={s.label} className="bg-card border border-border rounded-xl p-4">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                    style={{ background: s.bg }}
                  >
                    <Icon name={s.icon} size={18} color={s.color} />
                  </div>
                  <p className="text-xl font-bold text-foreground">{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Tabs + content */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex min-w-max">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-primary border-b-2 border-primary bg-primary/5'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab.icon} size={15} />
                    <span>{tab.label}</span>
                    {tab.id === 'trips' && activeTrips > 0 && !loading && (
                      <span
                        className="ml-1 px-1.5 py-0.5 rounded-full text-white font-bold"
                        style={{ background: '#059669', fontSize: '10px' }}
                      >
                        {activeTrips}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-4 sm:p-6">{renderContent()}</div>
          </div>

        </div>
      </div>
    </>
  );
};

export default TransporterDashboard;
